/**
 * Utility functions for parsing markdown content from compliance rules
 */

/**
 * Extracts the "Rule" section from markdown content.
 * The Rule section starts with **Rule** and continues until the next bold heading
 * (like **Applies when**, **What good looks like**, **Why it matters**) or end of string.
 *
 * @param markdown - The full markdown content
 * @returns The extracted Rule section including the heading, or empty string if not found
 */
export function extractRuleSection(markdown: string): string {
  if (!markdown || !markdown.trim()) {
    return '';
  }

  // Pattern to match the **Rule** heading. Accepts the colon either inside
  // or outside the bold delimiters — backend formatting commonly emits
  // `**Rule:**\n…` and we'd previously return '' on those inputs.
  const ruleHeadingPattern = /\*\*Rule:?\*\*:?\s*\n?/i;

  // Find the Rule heading
  const ruleMatch = markdown.match(ruleHeadingPattern);
  if (!ruleMatch) {
    return '';
  }

  const ruleStartIndex = ruleMatch.index! + ruleMatch[0].length;

  // Find the start of the next section: any bold heading after the Rule
  // body. A non-global regex is intentional — `String.prototype.match()`
  // with `/g` returns just the matched strings without `.index`, so the
  // previous global fallback never resolved a position and the function
  // fell through to "everything from Rule onward". One non-global pattern
  // covers all the documented variants (Applies when / What good looks
  // like / Why it matters) and any other bold heading.
  const nextSectionPattern = /\*\*[^*]+\*\*:?\s*\n?/i;

  let nextSectionIndex = markdown.length;
  const nextMatch = markdown.substring(ruleStartIndex).match(nextSectionPattern);
  if (nextMatch && nextMatch.index !== undefined) {
    nextSectionIndex = ruleStartIndex + nextMatch.index;
  }

  // Extract the Rule section (including the heading)
  const ruleSection = markdown.substring(ruleMatch.index!, nextSectionIndex).trim();
  
  return ruleSection || '';
}

